import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, ClipboardCheck, FlaskConical, Info, LockKeyhole, MapPin, Package, PackageCheck, Workflow, X } from 'lucide-react';
import { assignLaboratoryWorkflows, loadLaboratoryCollection } from './api';
import LaboratoryAcceptance from './LaboratoryAcceptance';
import { ContainerSymbol } from './XomicsCollections';
import { TubeState, labError, labWhen } from './LaboratoryParts';
import { departmentShort, ownSampleTypeName, workflowName } from './LaboratoryText';
import { Failed, Loading, Pill } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';

const text = (key, values) => tx(`ui.lab.assign.${key}`, values);
const receipt = (key, values) => tx(`ui.lab.inventory.${key}`, values);
const ACCEPTED = ['accepted', 'accepted_conditional'];
const STEPS = ['received', 'acceptance', 'workflow', 'work'];
const STEP_ICONS = { received: PackageCheck, acceptance: ClipboardCheck, workflow: Workflow, work: FlaskConical };
const ORDER_TONES = { completed: 'ok', stopped: 'bad', awaiting_approval: 'warn', awaiting_customer: 'warn', in_progress: 'info', planned: 'flat' };

// One state for a collection, from the tubes that have arrived: what the lab
// has to do next. A tube stands on its own, so tubes still on their way hold
// nothing up.
export function collectionState(row) {
  const states = row.physicalSamples.filter(sample => sample.receivedAt !== null).map(sample => sample.acceptanceState);
  if (states.some(state => ['awaiting_acceptance', 'awaiting_approval'].includes(state))) return 'awaiting_acceptance';
  if (states.some(state => state === 'on_hold')) return 'on_hold';
  if (row.assignmentCount > 0) return 'assigned';
  if (states.length > 0 && states.every(state => ['quarantined', 'new_sample_requested'].includes(state))) return 'quarantined';
  return 'ready';
}

export function CollectionState({ row }) {
  const state = collectionState(row);
  const tone = { awaiting_acceptance: 'info', on_hold: 'warn', quarantined: 'bad', assigned: 'ok', ready: 'info' }[state];
  return <Pill tone={tone}>{state === 'assigned' ? <Check aria-hidden="true" /> : <Workflow aria-hidden="true" />}
    {receipt(`state.${state}`)}</Pill>;
}

// The tubes of a collection as drawings: the container, whether it has arrived
// and how its acceptance went. A tube is numbered only beside other tubes.
export function TubeProgress({ samples }) {
  return <span className="xo-lab-tubes">{samples.map(sample => {
    const state = sample.acceptanceState;
    const description = receipt('tube_status', { code: sample.printedCode ?? sample.code, status: tx(`ui.lab.state.${state}`) });
    const mark = ACCEPTED.includes(state) ? <Check aria-hidden="true" /> : ['quarantined', 'new_sample_requested'].includes(state) ? <X aria-hidden="true" />
      : samples.length > 1 ? sample.sequenceNo : null;
    return <span key={sample.uuid} className={`xo-lab-tube${sample.receivedAt !== null ? ' is-received' : ''} is-${state}`} role="img" aria-label={description} title={description}>
      <ContainerSymbol code={sample.container.code} />
      {mark !== null && <span className="xo-lab-tube-mark">{mark}</span>}
    </span>;
  })}</span>;
}

// Where one tube is on its way through the laboratory. Each step is done, the
// one the laboratory does next, the one its way ended at, or still ahead.
export function tubePath(sample, orders) {
  const state = sample.acceptanceState, received = sample.receivedAt !== null;
  // A tube whose lab work was stopped had been accepted first.
  const accepted = ACCEPTED.includes(state) || (state === 'new_sample_requested' && orders.length > 0);
  const refused = state === 'quarantined' || (state === 'new_sample_requested' && orders.length === 0);
  const open = orders.some(order => !['completed', 'stopped'].includes(order.status));
  return {
    received: received ? 'done' : 'current',
    acceptance: !received ? 'upcoming' : accepted ? 'done' : refused ? 'stopped' : 'current',
    workflow: !accepted ? 'upcoming' : orders.length > 0 ? 'done' : 'current',
    work: orders.length === 0 ? 'upcoming' : open ? 'current' : orders.some(order => order.status === 'completed') ? 'done' : 'stopped',
  };
}

// The step a dialog opens on: what the laboratory does next, or how far the tube got.
export function pathFocus(path) {
  return STEPS.find(step => path[step] === 'current') || [...STEPS].reverse().find(step => path[step] !== 'upcoming');
}

// The tube a dialog opens on: the first one the laboratory has something to do with.
export function tubeFocus(samples, workOrders) {
  const waiting = samples.find(sample => sample.receivedAt !== null
    && Object.values(tubePath(sample, workOrders.filter(order => order.tube.uuid === sample.uuid))).includes('current'));
  return waiting || samples.find(sample => sample.receivedAt !== null) || samples[0];
}

function stepDetail(step, sample, path, orders) {
  if (step === 'received') return sample.receivedAt === null ? tx('ui.lab.state.awaiting_receipt') : labWhen(sample.receivedAt);
  if (step === 'acceptance') return path.acceptance === 'upcoming' ? '' : tx(`ui.lab.state.${path.acceptance === 'done' && !ACCEPTED.includes(sample.acceptanceState) ? 'accepted' : sample.acceptanceState}`);
  if (step === 'workflow') return orders.length === 0 ? tx('ui.lab.step.workflow_none') : orders.map(order => order.workflow.code).join(' · ');
  return orders.length === 0 ? tx('ui.lab.step.work_none') : tx('ui.lab.work.progress', {
    done: orders.reduce((sum, order) => sum + order.completedSteps, 0), total: orders.reduce((sum, order) => sum + order.totalSteps, 0) });
}

function Path({ sample, path, orders, active, onStep }) {
  return <ol className="xo-lab-path" role="tablist" aria-label={tx('ui.lab.step.label')}>{STEPS.map(step => {
    const Icon = path[step] === 'done' ? Check : path[step] === 'stopped' ? X : STEP_ICONS[step];
    return <li key={step} className={`is-${path[step]}`} role="presentation">
      <button type="button" role="tab" id={`lab-path-${step}`} aria-controls="lab-path-panel" aria-selected={active === step} onClick={() => onStep(step)}>
        <span className="xo-lab-path-mark"><Icon aria-hidden="true" /></span>
        <span className="xo-lab-path-text"><strong>{tx(`ui.lab.step.${step}`)}</strong><small>{stepDetail(step, sample, path, orders)}</small></span>
      </button>
    </li>;
  })}</ol>;
}

const Locked = ({ children }) => <p className="xo-lab-modal-note"><LockKeyhole aria-hidden="true" />{children}</p>;

function ReceiptPanel({ collection, sample }) {
  if (sample.receivedAt === null) return <Locked>{tx('ui.lab.receipt.receive_first')}</Locked>;
  return <dl className="xo-lab-modal-facts">
    <div><dt>{receipt('received_at')}</dt><dd>{labWhen(sample.receivedAt)}</dd></div>
    {sample.receivedBy !== null && <div><dt>{receipt('received_by')}</dt><dd>{sample.receivedBy.name}</dd></div>}
    {sample.device !== null && <div><dt>{receipt('scanner')}</dt><dd><code>{sample.device.deviceId}</code><small>{sample.device.model}</small></dd></div>}
    <div><dt>{tx('ui.lab.accept.container_type')}</dt><dd>{sample.container.name}</dd></div>
    {sample.organizationCode && <div><dt>{tx('ui.lab.accept.their_code', { organization: collection.organization.name })}</dt><dd><code>{sample.organizationCode}</code></dd></div>}
    {collection.activatedAt !== null && <div><dt>{tx('ui.collections.activated')}</dt><dd>{labWhen(collection.activatedAt)}</dd></div>}
  </dl>;
}

// The workflow a tube was sent for is chosen for it: what can still be assigned.
export const preselectedKeys = tube => !tube ? [] : tube.workflows.filter(option => option.preselected && option.available && option.workflow)
  .map(option => `${tube.uuid}:${option.workflow.versionId}`);

// Why the tube's workflow is preselected: its kit, its clinic barcode, its customer's order, or what its code was made for.
export function setReason(set, tube) {
  const option = tube.workflows.find(item => item.preselected);
  const workflow = option && option.workflow ? workflowName(option.workflow.code) : set.analysis.name;
  return text(`set.${set.source}`, { workflow, product: set.product, tube: set.clinicTube, organization: set.organization, project: set.project });
}

function WorkflowPanel({ data, tube, path, chosen, saving, onToggle }) {
  if (path.workflow === 'upcoming') return <Locked>{text('waiting_hint')}</Locked>;
  if (tube.workflows.length === 0) return <p className="xo-lab-modal-note"><Info aria-hidden="true" />{text(data.pipelines.length === 0 ? 'no_workflow' : 'no_analysis')}</p>;
  const set = data.set && tube.workflows.some(option => option.preselected) ? data.set : null;
  const forced = preselectedKeys(tube);
  return <>
    {set !== null ? <p className="xo-lab-modal-note is-set"><LockKeyhole aria-hidden="true" /><span><strong>{text('set_title')}</strong>{setReason(set, tube)}</span></p>
      : <p className="xo-lab-modal-hint">{text('choose_hint')}</p>}
    <ul className="xo-lab-workflow-options">{tube.workflows.map(option => {
      const key = option.workflow ? `${tube.uuid}:${option.workflow.versionId}` : `${tube.uuid}:${option.analysis.code}`;
      const assigned = option.reason === 'LAB_WORKFLOW_ALREADY_ASSIGNED';
      const locked = forced.includes(key);
      const code = option.workflow ? option.workflow.code : option.analysis.code;
      return <li key={key} className={`${option.available ? '' : 'is-unavailable'}${assigned ? ' is-assigned' : ''}${chosen.includes(key) || locked ? ' is-chosen' : ''}${option.preselected ? ' is-preselected' : ''}`}>
        <label><input type="checkbox" disabled={locked || !option.available || !data.mayAssign || saving} checked={locked || chosen.includes(key) || assigned}
          onChange={() => onToggle(key)} aria-label={text('choose_workflow', { workflow: code, tube: tube.printedCode ?? tube.code })} />
          <span className="xo-lab-workflow-code">{code}</span>
          <span className="xo-lab-workflow-name"><strong>{option.workflow ? workflowName(option.workflow.code) : option.analysis.name}</strong>
            {option.workflow && <small>{departmentShort(option.workflow.departmentCode)}</small>}</span>
          {assigned ? <Pill tone="ok"><Check aria-hidden="true" />{tx(`ui.lab.reason.${option.reason}`)}</Pill>
            : locked ? <Pill tone="info"><LockKeyhole aria-hidden="true" />{text('preselected')}</Pill>
              : !option.available && <em>{tx(`ui.lab.reason.${option.reason}`)}</em>}
        </label>
      </li>;
    })}</ul>
  </>;
}

function WorkPanel({ orders, archived, collectionUuid }) {
  return <>
    {orders.length === 0 ? <Locked>{text('no_work_orders')}</Locked>
      : <ul className="xo-lab-order-list">{orders.map(order => <li key={order.uuid}>
        <Link to={`/xomics/laboratory/work?collection=${encodeURIComponent(collectionUuid)}`}>
          <span className="xo-lab-workflow-code">{order.workflow.code}</span>
          <span className="xo-lab-workflow-name"><strong>{workflowName(order.workflow.code)}</strong>
            <small>{tx('ui.lab.work.progress', { done: order.completedSteps, total: order.totalSteps })}</small></span>
          <span className="xo-lab-order-steps" aria-hidden="true">{order.stages.map(stage => <i key={stage.id} className={`is-${stage.state}`} />)}</span>
          <Pill tone={ORDER_TONES[order.status] || 'flat'}>{tx(`ui.lab.stage.order.${order.status}`)}</Pill><ChevronRight aria-hidden="true" />
        </Link>
      </li>)}</ul>}
    {archived > 0 && <p className="xo-lab-modal-note"><Info aria-hidden="true" />{text('archived_count', { count: archived })}</p>}
  </>;
}

export default function LaboratoryCollectionModal({ collectionUuid, code, mayTrack, onClose, onAssigned }) {
  const load = useCallback(() => loadLaboratoryCollection(collectionUuid), [collectionUuid]);
  const resource = useLiveResource(['samples', 'pipelines'], load);
  const data = resource.data;
  // The tube and the step shown follow the laboratory's next action until one is picked.
  const [tubeUuid, setTubeUuid] = useState(null), [step, setStep] = useState(null), [chosen, setChosen] = useState([]);
  const [saving, setSaving] = useState(false), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const dialog = useRef(null), closing = useRef(onClose), inFlight = useRef(false);
  closing.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement, previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; dialog.current.focus();
    const onKey = event => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') { event.preventDefault(); closing.current(); return; }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.current.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), summary')].filter(element => element.getClientRects().length > 0);
      const first = controls[0], last = controls[controls.length - 1];
      if (document.activeElement === dialog.current || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; previousFocus?.focus(); };
  }, []);
  const samples = data === null ? [] : data.collection.physicalSamples;
  const sample = data === null ? null : samples.find(item => item.uuid === tubeUuid) || tubeFocus(samples, data.workOrders);
  const tube = sample === null ? null : data.tubes.find(item => item.uuid === sample.uuid);
  const orders = sample === null ? [] : data.workOrders.filter(order => order.tube.uuid === sample.uuid);
  const path = sample === null ? null : tubePath(sample, orders);
  const active = path === null ? null : step !== null ? step : pathFocus(path);
  const selection = [...new Set([...chosen, ...preselectedKeys(tube)])].map(key => {
    const [sampleUuid, version] = key.split(':');
    return { sampleUuid, workflowVersionId: Number(version) };
  }).filter(item => Number.isSafeInteger(item.workflowVersionId));
  function show(uuid) { setTubeUuid(uuid); setStep(null); setNotice(''); }
  async function assign() {
    if (inFlight.current || selection.length === 0) return;
    inFlight.current = true; setSaving(true); setError(''); setNotice('');
    try {
      await assignLaboratoryWorkflows(collectionUuid, selection);
      setChosen([]); setNotice(text('saved')); setStep(null); onAssigned(); await resource.refresh();
    } catch (caught) { setError(labError(caught)); }
    finally { inFlight.current = false; setSaving(false); }
  }
  return <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="xo-product-editor xo-lab-modal" role="dialog" aria-modal="true" aria-labelledby="lab-collection-title" tabIndex={-1} ref={dialog}>
      <header><div><h2 id="lab-collection-title">{tx('ui.collections.detail_title', { code })}</h2></div>
        <button type="button" className="xo-icon-btn" onClick={onClose} aria-label={tx('ui.collections.close')}><X aria-hidden="true" /></button></header>
      <div className="xo-lab-modal-body">
        {resource.error ? <Failed message={resource.error} onRetry={resource.refresh} /> : data === null ? <Loading label={tx('ui.lab.loading')} /> : <>
          <div className="xo-lab-modal-identity">
            <TubeProgress samples={samples} />
            <div><CollectionState row={data.collection} /><strong>{ownSampleTypeName(data.collection.sampleType)}</strong>
              {(data.collection.product !== null || data.collection.organization !== null) && <span className="xo-lab-modal-meta">
                {data.collection.product !== null && <span><Package aria-hidden="true" />{data.collection.product.name}</span>}
                {data.collection.organization !== null && <span>{data.collection.organization.name}</span>}</span>}
            </div>
          </div>
          {samples.length > 1 && <div className="xo-lab-modal-tubes" role="group" aria-label={text('tubes')}>
            {samples.map(item => <button type="button" key={item.uuid} aria-pressed={item.uuid === sample.uuid} className={item.uuid === sample.uuid ? 'is-active' : ''}
              onClick={() => show(item.uuid)}><code>{item.printedCode ?? item.code}</code><TubeState value={item.acceptanceState} /></button>)}
          </div>}
          <Path sample={sample} path={path} orders={orders} active={active} onStep={value => { setStep(value); setNotice(''); }} />
          <div id="lab-path-panel" role="tabpanel" aria-labelledby={`lab-path-${active}`} className="xo-lab-modal-panel">
            {active === 'received' ? <ReceiptPanel collection={data.collection} sample={sample} />
              : active === 'acceptance' ? (path.acceptance === 'upcoming' ? <Locked>{tx('ui.lab.receipt.receive_first')}</Locked>
                : <LaboratoryAcceptance key={sample.uuid} embedded sampleUuid={sample.uuid} onRecorded={result => {
                  setNotice(tx(`ui.lab.accept.recorded.${result.tube.state}`)); setStep(null); onAssigned(); resource.refresh();
                }} />)
                : active === 'workflow' ? <WorkflowPanel data={data} tube={tube} path={path} chosen={chosen} saving={saving}
                  onToggle={key => { setChosen(current => current.includes(key) ? current.filter(value => value !== key) : [...current, key]); setNotice(''); }} />
                  : <WorkPanel orders={orders} archived={data.archivedWorkOrderCount} collectionUuid={collectionUuid} />}
          </div>
        </>}
      </div>
      <footer className="xo-lab-modal-footer">
        {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
        {notice !== '' && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
        <div>
          {mayTrack && <Link className="xo-btn-2" to={`/xomics/sample-tracking?sampleUuid=${encodeURIComponent(collectionUuid)}`}><MapPin aria-hidden="true" />{tx('ui.kits.open_sample_tracking')}</Link>}
          <span className="xo-lab-modal-actions">
            <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.collections.close')}</button>
            {data !== null && active === 'workflow' && data.mayAssign && path.workflow !== 'upcoming' && tube.workflows.length > 0
              && <button type="button" className="xo-btn" onClick={assign} disabled={saving || selection.length === 0}>
                <Workflow aria-hidden="true" />{text(saving ? 'assigning' : selection.length === 0 ? 'choose' : 'assign', { count: selection.length })}</button>}
            {data !== null && active === 'work' && orders.length > 0 && <Link className="xo-btn" to={`/xomics/laboratory/work?collection=${encodeURIComponent(collectionUuid)}`}>
              <FlaskConical aria-hidden="true" />{tx('ui.lab.work.open_work')}</Link>}
          </span>
        </div>
      </footer>
    </section>
  </div>;
}
